import { useEffect } from 'react';
import {
  $getRoot,
  $isElementNode,
  $isTextNode,
  $createTextNode,
  COMMAND_PRIORITY_HIGH,
  type EditorState,
  LexicalCommand,
  PASTE_COMMAND,
  TextNode,
  LexicalNode,
} from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { tryParseHttpUrl } from '../lib/links/shared';
import { $createLinkPreviewNodeFromEditor, LinkPreviewNode } from '../nodes/LinkPreviewNode';
import { debounce } from '../lib/links/shared';
import { TIMING } from '../constants/ui';

// Find all URLs in a string (token-based, not greedy regex)
function findAllUrls(text: string): Array<{ url: string; start: number; end: number }> {
  const out: Array<{ url: string; start: number; end: number }> = [];
  const tokens = Array.from(text.matchAll(/\S+/g));
  for (const m of tokens) {
    const candidate = m[0];
    const u = tryParseHttpUrl(candidate);
    if (u) out.push({ url: u.toString(), start: m.index || 0, end: (m.index || 0) + candidate.length });
  }
  return out;
}

export default function LinkDetectionPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const removePaste = editor.registerCommand(
      PASTE_COMMAND as unknown as LexicalCommand<ClipboardEvent>,
      (event: ClipboardEvent) => {
        // Allow the paste to insert text, then append previews for any URLs
        const text = event.clipboardData?.getData('text/plain') || '';
        const urls = findAllUrls(text);
        if (urls.length === 0) return false; // Let default paste proceed
        // Let default paste happen, then convert URLs to LinkPreviewNodes
        setTimeout(() => {
          scheduleTypedConversion();
        }, 0);
        return false; // don't block the paste
      },
      COMMAND_PRIORITY_HIGH
    );

    const scheduleTypedConversion = debounce(() => {
      editor.update(() => {
        const root = $getRoot();
        
        // Replace URL text nodes with LinkPreviewNode instances
        root.getChildren().forEach((child) => {
          if ($isElementNode(child)) {
            const textNodes = child.getChildren().filter((grand): grand is TextNode => 
              $isTextNode(grand)
            );
            
            textNodes.forEach((textNode) => {
              const text = textNode.getTextContent();
              const urls = findAllUrls(text);
              
              if (urls.length > 0) {
                // Split text around URLs and replace with LinkPreviewNode instances
                let currentPos = 0;
                const newNodes: LexicalNode[] = [];
                
                urls.forEach(({ url, start, end }) => {
                  // Add text before URL
                  if (start > currentPos) {
                    const beforeText = text.slice(currentPos, start);
                    if (beforeText) {
                      newNodes.push($createTextNode(beforeText));
                    }
                  }
                  
                  // Add LinkPreviewNode for URL
                  if (!editor.hasNodes([LinkPreviewNode])) return;
                  const linkNode = $createLinkPreviewNodeFromEditor(editor, url);
                  newNodes.push(linkNode);
                  
                  currentPos = end;
                });
                
                // Add remaining text after last URL
                if (currentPos < text.length) {
                  const afterText = text.slice(currentPos);
                  if (afterText) {
                    newNodes.push($createTextNode(afterText));
                  }
                }
                
                // Replace the original text node with new nodes
                if (newNodes.length > 0) {
                  const parent = textNode.getParent();
                  if (parent) {
                    const index = textNode.getIndexWithinParent();
                    textNode.remove();
                    newNodes.forEach((node, i) => {
                      parent.splice(index + i, 0, [node]);
                    });
                  }
                }
              }
            });
          }
        });
      });
    }, TIMING.LINK_DETECTION_DEBOUNCE);

    const removeListener = editor.registerUpdateListener(({ editorState }: { editorState: EditorState }) => {
      // Debounced detection for typed URLs at end of text
      scheduleTypedConversion();
    });

    return () => {
      removePaste();
      removeListener();
    };
  }, [editor]);

  return null;
}
